"use client";

// French copy of components/widgetsLibraryV2 for /fr/widgets.
// Lists every widget, like the English page. Cards for widgets with a French
// page use its French name and link there; the rest keep their English name,
// link to the English page and carry an EN tag. Every description, category
// and label is French (i18n/fr/library.ts). The platform-page variant of the
// English component is left out: the French site has no platform pages yet.

import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { FiSearch } from "@react-icons/all-files/fi/FiSearch";
import { FiX } from "@react-icons/all-files/fi/FiX";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import { FiGrid } from "@react-icons/all-files/fi/FiGrid";
import { FiStar } from "@react-icons/all-files/fi/FiStar";
import { FiLayout } from "@react-icons/all-files/fi/FiLayout";
import { FiClipboard } from "@react-icons/all-files/fi/FiClipboard";
import { FiRss } from "@react-icons/all-files/fi/FiRss";
import { FiMessageCircle } from "@react-icons/all-files/fi/FiMessageCircle";
import { FiImage } from "@react-icons/all-files/fi/FiImage";
import { FiClock } from "@react-icons/all-files/fi/FiClock";
import { FiAward } from "@react-icons/all-files/fi/FiAward";

import type { ILibraryCategory } from "../../helpers/widgetsLibraryData";
import type { IEsLibraryApp } from "@/i18n/fr/library";
import { frHref } from "@/i18n/fr/routes";
import { searchWidgetsFr } from "@/i18n/fr/search";
import { SocialProofFr } from "./socialProofFr.comp";
import { FaqFr } from "./faqFr.comp";
import { EnTag } from "./langSwitch.comp";
import { DEFAULT_PLATFORM_TILES } from "../widgetPlatformCarousel/widgetPlatformCarousel.data";
import { StyledWidgetsLibraryV2 } from "../widgetsLibraryV2/widgetsLibraryV2.styles";

interface IWidgetsLibraryEsProps {
  apps: IEsLibraryApp[];
  // Category names are already French (i18n/fr/library.ts).
  categories: ILibraryCategory[];
}

const LIST_PATH = frHref("/widgets");

const PAGE_SIZE = 24;
const SUGGESTION_LIMIT = 8;
const NEW_DAYS = 90;
const FEATURED_TAGS = ["Popular", "Trending"];
// Hand-picked by Sergei (2026-09-29), shown in this order.
const TOP_WIDGET_SLUGS = [
  "google-reviews",
  "audio-player",
  "brackets",
  "calendar",
  "age-verification",
  "logo-showcase",
  "before-after",
  "image-hotspot",
  "timeline",
  "countdown",
  "instagram-feed",
  "ai-chatbot",
];
// Where the faint platform marks sit in the hero background: left and right
// edges only, so nothing sits behind the headline or the search box.
const HERO_MARKS = [
  { x: 4, y: 14, size: 34, r: -8 },
  { x: 13, y: 34, size: 26, r: 6 },
  { x: 6, y: 56, size: 40, r: 4 },
  { x: 15, y: 74, size: 28, r: -6 },
  { x: 3, y: 88, size: 24, r: 10 },
  { x: 20, y: 12, size: 22, r: 4 },
  { x: 22, y: 90, size: 30, r: -4 },
  { x: 93, y: 12, size: 30, r: 8 },
  { x: 84, y: 30, size: 24, r: -6 },
  { x: 92, y: 50, size: 38, r: -4 },
  { x: 83, y: 70, size: 30, r: 6 },
  { x: 94, y: 86, size: 26, r: -10 },
  { x: 76, y: 10, size: 22, r: -4 },
  { x: 75, y: 90, size: 28, r: 6 },
];
// One line icon per category chip (design B, 2026-09-29).
const CATEGORY_ICONS: Record<string, React.ComponentType> = {
  "Avis et confiance": FiStar,
  "Popups et barres": FiLayout,
  "Formulaires et réservations": FiClipboard,
  "Flux sociaux": FiRss,
  "Chat et contact": FiMessageCircle,
  "Images et vidéo": FiImage,
  "Minuteurs et compteurs": FiClock,
  "Jeux et quiz": FiAward,
};

// ?category= links that use the English names (the English page's groups
// and its older DB category names) open the matching French group.
const LEGACY_CATEGORY_LINKS: Record<string, string> = {
  "Reviews & trust": "Avis et confiance",
  "Popups & bars": "Popups et barres",
  "Forms & bookings": "Formulaires et réservations",
  "Social feeds": "Flux sociaux",
  "Chat & contact": "Chat et contact",
  "Images & video": "Images et vidéo",
  "Timers & counters": "Minuteurs et compteurs",
  "Games & quizzes": "Jeux et quiz",
  Reviews: "Avis et confiance",
  "Popups, Bars & Buttons": "Popups et barres",
  Forms: "Formulaires et réservations",
  "Social Feeds": "Flux sociaux",
  Chats: "Chat et contact",
  "Images & Galleries": "Images et vidéo",
  Media: "Images et vidéo",
  Gaming: "Jeux et quiz",
};

const QUICK_SEARCHES = [
  "Avis Google",
  "Flux Instagram",
  "Formulaire de contact",
  "Compte à rebours",
];

// Badge text; the class name keeps the English word, which the styles target.
const BADGE_FR: Record<string, string> = {
  New: "Nouveau",
  Popular: "Populaire",
  Trending: "Tendance",
};

const FAQS = [
  {
    q: "Qu'est-ce qu'un widget de site web ?",
    a: "Un widget de site web est une petite fonctionnalité autonome que vous ajoutez à une page, comme un mur d'avis, un flux Instagram, un formulaire de contact ou un compte à rebours. Vous le concevez dans l'éditeur Common Ninja, puis vous collez une ligne de code d'intégration à l'endroit où il doit apparaître.",
  },
  {
    q: "Les widgets Common Ninja sont-ils gratuits ?",
    a: "Oui. Une offre gratuite est disponible pour tous nos widgets, sans carte bancaire. Les offres payantes ajoutent des fonctionnalités premium et lèvent les limites quand vous en avez besoin.",
  },
  {
    q: "Sur quelles plateformes les widgets fonctionnent-ils ?",
    a: "Ils fonctionnent sur plus de 200 plateformes, dont WordPress, Shopify, Wix, Squarespace, Webflow et les sites en HTML simple. Si votre site vous permet de coller un code d'intégration, vous pouvez y ajouter un widget Common Ninja.",
  },
  {
    q: "Faut-il savoir coder ?",
    a: "Non. Chaque widget se personnalise visuellement dans l'éditeur : mise en page, couleurs, polices et contenu. Quand le résultat vous convient, copiez le code d'intégration et collez-le sur votre site. Les modifications que vous faites ensuite se mettent à jour automatiquement.",
  },
  {
    q: "Vous ne trouvez pas le widget qu'il vous faut ?",
    a: 'Essayez <a href="https://embeddable.co?ref=commonninja" target="_blank" hreflang="en">Embeddable</a> (en anglais), notre créateur de widgets par IA. Décrivez ce dont vous avez besoin et il crée un widget sur mesure pour vous.',
  },
];

function isNew(app: IEsLibraryApp) {
  if (!app.created) return false;
  return Date.now() - new Date(app.created).getTime() < NEW_DAYS * 86400000;
}

function badgeFor(app: IEsLibraryApp) {
  if (isNew(app)) return "New";
  return app.tags.find((t) => FEATURED_TAGS.includes(t));
}

const categoryHref = (name: string) =>
  `${LIST_PATH}?category=${encodeURIComponent(name)}`;

const linkFor = (app: IEsLibraryApp) => app.href;

function WidgetCard({ app, eager }: { app: IEsLibraryApp; eager?: boolean }) {
  const badge = badgeFor(app);
  return (
    <Link
      href={linkFor(app)}
      className={`card ${badge === "New" ? "card-new" : ""}`}
      title={`Widget ${app.name}`}
      hrefLang={app.isEn ? "en" : "fr"}
    >
      <span className="card-preview">
        <Image
          src={app.preview || app.logo}
          alt={`Aperçu du widget ${app.name}`}
          width={320}
          height={170}
          priority={eager}
          loading={eager ? undefined : "lazy"}
        />
        {badge && (
          <span className={`badge badge-${badge.toLowerCase()}`}>
            {BADGE_FR[badge] || badge}
          </span>
        )}
      </span>
      <span className="card-body">
        <span className="card-title">
          <Image src={app.logo} alt="" width={28} height={28} />
          <span>
            <strong>
              {app.name}
              {app.isEn && <EnTag />}
            </strong>
            {app.categories[0] && <small>{app.categories[0]}</small>}
          </span>
        </span>
        <span className="card-desc">{app.teaser}</span>
      </span>
    </Link>
  );
}

export const WidgetsLibraryFr = ({
  apps,
  categories,
}: IWidgetsLibraryEsProps) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const gridRef = useRef<HTMLElement>(null);
  const chipsSentinel = useRef<HTMLDivElement>(null);
  const [chipsStuck, setChipsStuck] = useState(false);
  const [chipsTop, setChipsTop] = useState(0);

  // The chip bar is sticky, and so is the site header: it hides while you
  // scroll down and floats back in (position: fixed) when you scroll up. So
  // the bar sits just under the header whenever the header is showing, and
  // at the very top otherwise. "Stuck" (the stronger floating look) means the
  // sentinel above the bar has scrolled past where the bar pins.
  useEffect(() => {
    let raf = 0;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const measure = () => {
      raf = 0;
      let offset = 0;
      document.querySelectorAll("header").forEach((h) => {
        const cs = window.getComputedStyle(h);
        if (cs.position !== "fixed" || cs.display === "none") return;
        const bottom = h.getBoundingClientRect().bottom;
        if (bottom > 0) offset = Math.max(offset, Math.round(bottom) + 8);
      });
      setChipsTop(offset);
      const sentinel = chipsSentinel.current;
      setChipsStuck(
        !!sentinel && sentinel.getBoundingClientRect().top < offset
      );
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
      // The header slides in over 0.3s; measure again once it has landed.
      clearTimeout(settle);
      settle = setTimeout(measure, 350);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, []);
  const urlReady = useRef(false);

  // Read ?search= / ?category= after mount, so the static HTML always holds
  // the full, unfiltered list of widget links for crawlers.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("search") || "";
    const c = params.get("category") || "";
    // Old ?category= links use the DB category names; send them to the group
    // that replaced them. Anything unknown falls back to "All".
    const name = LEGACY_CATEGORY_LINKS[c] || c;
    const match = categories.find(
      (cat) => cat.name.toLowerCase() === name.toLowerCase()
    );
    if (s) setQuery(s);
    if (match) setCategory(match.name);
  }, [categories]);

  useEffect(() => {
    // Skip the first run, so the effect above reads the URL before it is rewritten.
    if (!urlReady.current) {
      urlReady.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (query.trim()) params.set("search", query.trim());
    else params.delete("search");
    if (category) params.set("category", category);
    else params.delete("category");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
    setVisible(PAGE_SIZE);
  }, [query, category]);

  const popular = useMemo(
    () =>
      TOP_WIDGET_SLUGS.map((slug) => apps.find((a) => a.enHref === `/widgets/${slug}`)).filter(
        (a): a is IEsLibraryApp => Boolean(a)
      ),
    [apps]
  );

  const newest = useMemo(
    () =>
      [...apps]
        .filter(isNew)
        .sort((a, b) => b.created.localeCompare(a.created))
        .slice(0, 4),
    [apps]
  );

  const matches = useMemo(
    () => (query.trim().length > 1 ? searchWidgetsFr(apps, query) : []),
    [apps, query]
  );
  const suggestions = matches.slice(0, SUGGESTION_LIMIT);

  // Keep the highlighted suggestion visible when arrowing through the list.
  useEffect(() => {
    if (activeSuggestion < 0) return;
    document
      .getElementById(`widget-suggestion-${activeSuggestion}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeSuggestion]);

  const results = useMemo(() => {
    if (!query.trim() && !category) {
      // The rows above already show these, so they go last instead of
      // repeating at the top. Every widget still appears exactly once.
      const shownAbove = new Set([...popular, ...newest].map((a) => a._id));
      return [
        ...apps.filter((a) => !shownAbove.has(a._id)),
        ...apps.filter((a) => shownAbove.has(a._id)),
      ];
    }
    const inCategory = category
      ? apps.filter((a) => a.categories.includes(category))
      : apps;
    return query.trim() ? searchWidgetsFr(inCategory, query) : inCategory;
  }, [apps, query, category, popular, newest]);

  const isFiltered = Boolean(query.trim() || category);
  const shown = Math.min(visible, results.length);

  // Picking a category or searching hides the Top widgets and Recently added
  // rows, which moves the grid up the page. Scrolling in the click handler
  // measured the old layout and overshot by the height of those rows, landing
  // at the bottom on short categories. So only ask for the scroll here, and do
  // it after React has drawn the new layout.
  const [scrollRequest, setScrollRequest] = useState(0);
  useEffect(() => {
    if (!scrollRequest) return;
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollRequest]);

  function scrollToGrid() {
    setScrollRequest((n) => n + 1);
  }

  function pickCategory(e: React.MouseEvent, name: string) {
    e.preventDefault();
    setCategory(name);
    setQuery("");
    scrollToGrid();
  }

  function runSearch(term: string) {
    setQuery(term);
    setCategory("");
    setSuggestOpen(false);
    scrollToGrid();
  }

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!suggestOpen || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeSuggestion >= 0) {
      e.preventDefault();
      window.location.href = linkFor(suggestions[activeSuggestion]);
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  }

  const gridTitle = category
    ? `Widgets : ${category}`
    : query.trim()
    ? `Résultats pour « ${query.trim()} »`
    : "Tous les widgets";

  return (
    <StyledWidgetsLibraryV2>
      <section className="hero">
        {/* Faint platform marks around the edges: the "any platform" line,
            said quietly by the background. Decorative only. */}
        <div className="hero-marks" aria-hidden="true">
          {HERO_MARKS.map((m, i) => {
            const p = DEFAULT_PLATFORM_TILES[i % DEFAULT_PLATFORM_TILES.length];
            return (
              <svg
                key={p.slug}
                viewBox="0 0 24 24"
                style={{
                  left: `${m.x}%`,
                  top: `${m.y}%`,
                  width: m.size,
                  height: m.size,
                  transform: `rotate(${m.r}deg)`,
                }}
              >
                <path d={p.iconPath} />
              </svg>
            );
          })}
        </div>
        <div className="hero-inner">
          <div className="hero-proof">
            <SocialProofFr avatarSize="small" />
          </div>
          <h1>
            <span className="gradient">Widgets gratuits</span> pour votre site.{" "}
            <span className="second-line">Toutes les plateformes, zéro code.</span>
          </h1>
          <p className="lead">
            Mettez en avant vos avis, collectez des leads, prenez des
            réservations et vendez plus avec des widgets prêts à l&apos;emploi.
            Adaptez chaque widget à votre marque dans l&apos;éditeur visuel, puis
            ajoutez une ligne de code sur WordPress, Shopify, Wix ou n&apos;importe
            quel autre site.
          </p>

          <form
            className="search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(query);
            }}
          >
            <FiSearch className="search-icon" aria-hidden />
            <input
              type="search"
              role="combobox"
              aria-label="Rechercher des widgets"
              aria-autocomplete="list"
              aria-expanded={suggestOpen && suggestions.length > 0}
              aria-controls="widget-suggestions"
              placeholder="Rechercher un widget, ex. avis, formulaire, compte à rebours"
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setCategory("");
                setSuggestOpen(true);
                setActiveSuggestion(-1);
              }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
              onKeyDown={onSearchKey}
            />
            <button type="submit">Rechercher</button>
            {suggestOpen && suggestions.length > 0 && (
              <div className="suggest-panel">
              <ul className="suggestions" id="widget-suggestions" role="listbox">
                {suggestions.map((app, i) => (
                  <li
                    key={app._id}
                    id={`widget-suggestion-${i}`}
                    role="option"
                    aria-selected={i === activeSuggestion}
                  >
                    <Link href={linkFor(app)}>
                      <Image src={app.logo} alt="" width={28} height={28} />
                      <span>
                        <strong>
                          {app.name}
                          {app.isEn && <EnTag />}
                        </strong>
                        <small>{app.categories[0]}</small>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              {matches.length > suggestions.length && (
                <button
                  type="button"
                  className="suggest-all"
                  // mousedown fires before the input's blur closes the panel
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => runSearch(query)}
                >
                  Voir les {matches.length} résultats <FiArrowRight />
                </button>
              )}
              </div>
            )}
          </form>

          <div className="quick">
            <span>Recherches populaires :</span>
            {QUICK_SEARCHES.map((term) => (
              <button key={term} type="button" onClick={() => runSearch(term)}>
                {term}
              </button>
            ))}
          </div>

          <div className="scores">
            <span>
              <span className="star" aria-hidden="true">★</span> <strong>4,9</strong> sur Google
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>
              <span className="star" aria-hidden="true">★</span> <strong>4,3</strong> sur Trustpilot
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>Offre gratuite pour chaque widget</span>
          </div>

        </div>
      </section>


      <div ref={chipsSentinel} aria-hidden="true" />
      <nav
        className={`chips ${chipsStuck ? "is-stuck" : ""}`}
        aria-label="Catégories de widgets"
        style={{ top: chipsTop }}
      >
        <div className="chips-track">
          <a
            href={LIST_PATH}
            className={`chip ${!category ? "active" : ""}`}
            onClick={(e) => pickCategory(e, "")}
          >
            <FiGrid aria-hidden="true" /> Tous
          </a>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.name] || FiGrid;
            return (
              <a
                key={cat.name}
                href={categoryHref(cat.name)}
                className={`chip ${category === cat.name ? "active" : ""}`}
                onClick={(e) => pickCategory(e, cat.name)}
              >
                <Icon aria-hidden="true" /> {cat.name}
              </a>
            );
          })}
        </div>
      </nav>

      <main className="main">
        {!isFiltered && (
          <>
            <section className="row">
              <div className="row-head">
                <h2>Widgets phares</h2>
                <p>Les widgets par lesquels la plupart des sites commencent.</p>
              </div>
              <div className="grid grid-scroll">
                {popular.map((app, i) => (
                  <WidgetCard key={app._id} app={app} eager={i < 4} />
                ))}
              </div>
            </section>

            {newest.length > 0 && (
              <section className="row">
                <div className="row-head">
                  <h2>Ajouts récents</h2>
                  <p>Les nouveaux widgets de ces derniers mois.</p>
                </div>
                <div className="grid grid-scroll">
                  {newest.map((app) => (
                    <WidgetCard key={app._id} app={app} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <section className="row" ref={gridRef} id="all-widgets">
          <div className="row-head row-head-split">
            <div>
              <h2>{gridTitle}</h2>
              <p aria-live="polite">
                {results.length
                  ? `${shown} widgets affichés sur ${results.length}`
                  : "Aucun widget correspondant"}
              </p>
            </div>
            {isFiltered && (
              <button
                type="button"
                className="clear"
                onClick={() => {
                  setQuery("");
                  setCategory("");
                }}
              >
                Effacer les filtres <FiX />
              </button>
            )}
          </div>

          {results.length > 0 ? (
            <div className="grid grid-compact">
              {results.map((app, i) => (
                <div key={app._id} className={i >= visible ? "is-hidden" : undefined}>
                  <WidgetCard app={app} />
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              <h3>Aucun widget ne correspond encore à « {query.trim()} »</h3>
              <p>Essayez l&apos;une de ces catégories, ou commencez par un widget populaire.</p>
              <div className="empty-chips">
                {categories.slice(0, 6).map((cat) => (
                  <a
                    key={cat.name}
                    href={categoryHref(cat.name)}
                    className="chip"
                    onClick={(e) => pickCategory(e, cat.name)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
              <div className="grid">
                {popular.slice(0, 4).map((app) => (
                  <WidgetCard key={app._id} app={app} />
                ))}
              </div>
              <p className="empty-embd">
                Besoin de quelque chose sur mesure ?{" "}
                <Link href="https://embeddable.co?ref=commonninja" target="_blank" hrefLang="en">
                  Créez-le avec Embeddable, notre créateur de widgets par IA
                  <EnTag />
                </Link>
              </p>
            </div>
          )}

          {results.length > visible && (
            <div className="more">
              <button type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Afficher {Math.min(PAGE_SIZE, results.length - visible)} widgets de plus
              </button>
            </div>
          )}
        </section>


        <section className="steps">
          <h2>
            Choisissez, personnalisez, <span className="gradient">intégrez</span>
          </h2>
          <ol>
            <li>
              <span className="step-num">1</span>
              <strong>Choisissez un widget</strong>
              <p>Parcourez la bibliothèque et partez d&apos;un modèle prêt à l&apos;emploi.</p>
            </li>
            <li>
              <span className="step-num">2</span>
              <strong>Personnalisez-le</strong>
              <p>Modifiez la mise en page, les couleurs, les polices et le contenu dans l&apos;éditeur visuel. Sans code.</p>
            </li>
            <li>
              <span className="step-num">3</span>
              <strong>Intégrez-le partout</strong>
              <p>
                Collez une ligne de code sur l&apos;une des plus de 200 plateformes.
                Les modifications ultérieures se mettent à jour d&apos;elles-mêmes.
              </p>
            </li>
          </ol>
          <Link href="/signup" className="steps-cta">
            Commencer gratuitement <FiArrowRight />
          </Link>
          <p className="steps-note">
            Gratuit à vie. Sans carte bancaire. L&apos;éditeur est en anglais pour
            le moment.
          </p>
        </section>

        <section className="faq-wrap">
          <FaqFr faqs={FAQS} />
        </section>
      </main>
    </StyledWidgetsLibraryV2>
  );
};
